// Deal-brokering venture maths — connect a buyer and a seller, earn commission.
//
// The one lesson no other venture teaches: YOU DON'T NEED CAPITAL TO MAKE
// MONEY — you need to create value between two people who haven't found each
// other, and the asset that compounds is your REPUTATION. Every deal you close
// makes the next one easier to close; a well-known broker closes almost
// everything they touch.
//
//   • Working a deal costs ENERGY (calls, legwork), never cash.
//   • The close chance is driven by reputation + track record, shown to the
//     player up front — expected value, not gambling: the odds are knowable,
//     improvable, and dominated by a stat the player builds.
//   • Deals are DERIVED from (rngSeed, week) like every other market — nothing
//     stored, nothing to save-scum. The luck draw advances the shared cursor
//     only when a deal actually resolves (mirrors gaming tournaments).

import { nextFloat } from '../rng/prng.js';

export const DEALS_SIZE = 2; // deals on the board each week
export const WORK_ENERGY = 15; // energy to work a deal (one per week)
export const CLOSE_REP_GAIN = 2; // reputation gained per closed deal

type DealTemplate = {
  defId: string;
  need: string; // who needs what
  supply: string; // who has it
  emoji: string;
  baseSize: number; // typical deal size (B$)
  commissionPct: number; // broker's cut of the deal size
};

const CATALOG: DealTemplate[] = [
  { defId: 'school-chairs', need: 'School needs 30 chairs', supply: 'Workshop has extra stock', emoji: '🪑', baseSize: 300, commissionPct: 0.1 },
  { defId: 'event-sound', need: 'Wedding needs a sound system', supply: 'DJ crew rents theirs out', emoji: '🔊', baseSize: 450, commissionPct: 0.1 },
  { defId: 'farm-produce', need: 'Market stall wants vegetables', supply: 'Farm has a surplus', emoji: '🥬', baseSize: 220, commissionPct: 0.12 },
  { defId: 'used-laptops', need: 'Cyber café needs 5 laptops', supply: 'Office is upgrading theirs', emoji: '💻', baseSize: 800, commissionPct: 0.08 },
  { defId: 'bricks', need: 'Builder short on bricks', supply: 'Yard closing down its stock', emoji: '🧱', baseSize: 600, commissionPct: 0.09 },
  { defId: 'catering', need: 'Party of 60 needs catering', supply: 'Kitchen with free weekends', emoji: '🍲', baseSize: 500, commissionPct: 0.1 },
  { defId: 'transport', need: 'Shop needs weekly deliveries', supply: 'Driver with an empty bakkie', emoji: '🛻', baseSize: 350, commissionPct: 0.11 },
  { defId: 'tutoring', need: 'Parents want maths tutoring', supply: 'Top student with spare hours', emoji: '📐', baseSize: 180, commissionPct: 0.14 },
  { defId: 'salon-chairs', need: 'New salon needs equipment', supply: 'Closed salon selling up', emoji: '💈', baseSize: 700, commissionPct: 0.09 },
  { defId: 'tent-hire', need: 'Funeral committee needs tents', supply: 'Hire company with idle stock', emoji: '⛺', baseSize: 400, commissionPct: 0.1 },
];

/** One deal on this week's board. */
export type DealOffer = {
  slot: number; // 0..DEALS_SIZE-1
  defId: string;
  need: string;
  supply: string;
  emoji: string;
  dealSize: number; // full value of the deal being brokered
  commission: number; // what the broker earns if it closes
};

const clamp = (n: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, n));

/** Deterministic float for a deal in a week — independent of the shared cursor. */
function draw(seed: number, week: number, key: number, k: number): number {
  return nextFloat(seed, week * 92821 + key * 29 + k).value;
}

/** The 2 deals on the board this week, derived from (seed, week). */
export function generateDeals(seed: number, week: number): DealOffer[] {
  const ranked = CATALOG.map((c, i) => ({ c, i, r: draw(seed, week, i, 0) }))
    .sort((a, b) => a.r - b.r)
    .slice(0, DEALS_SIZE);
  return ranked.map((entry, slot) => {
    const c = entry.c;
    const dealSize = Math.round(c.baseSize * (0.8 + 0.5 * draw(seed, week, entry.i, 1))); // [0.8, 1.3]×
    return {
      slot,
      defId: c.defId,
      need: c.need,
      supply: c.supply,
      emoji: c.emoji,
      dealSize,
      commission: Math.max(5, Math.round(dealSize * c.commissionPct)),
    };
  });
}

/** Chance this broker closes a deal — knowable up front, dominated by stats the
 *  player builds. Reputation (0..100) is the big lever; every past close adds a
 *  little track-record edge. Capped so there's always SOME deal risk. */
export function closeChance(reputation: number, dealsClosed: number): number {
  return clamp(0.35 + reputation / 200 + Math.min(0.15, dealsClosed * 0.015), 0.35, 0.85);
}

/** Broker "network level" label thresholds — pure presentation helper. */
export function networkLevel(dealsClosed: number): { label: string; next: number | null } {
  if (dealsClosed >= 15) return { label: 'Rainmaker', next: null };
  if (dealsClosed >= 8) return { label: 'Connector', next: 15 };
  if (dealsClosed >= 3) return { label: 'Hustler', next: 8 };
  return { label: 'Rookie', next: 3 };
}
